import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSchool } from "@/context/SchoolContext";
import { hasPerm, initials, setDateFormat } from "@/lib/helpers";
import api, { apiErr } from "@/lib/api";
import { useSAMode, setSAMode } from "@/lib/saMode";
import { useDrill, setDrill } from "@/lib/drill";
import { toast } from "sonner";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import MenuColorSwitcher from "@/components/MenuColorSwitcher";
import FingerprintPrompt from "@/components/FingerprintPrompt";
import {
  LayoutDashboard, LayoutTemplate, Users2, School, ReceiptIndianRupee, HandCoins, AlertCircle,
  PhoneCall, Bus, CalendarCheck, Wallet, BarChart3, UserCog, Settings as SettingsIcon,
  ScrollText, Menu, X, LogOut, GraduationCap, KeyRound, IdCard, ChevronDown, Trash2, Table2,
  BookOpen, ClipboardList, PencilLine, FileText, PanelLeft, PanelLeftClose, Rows3, Columns3, Info, Search, Award, Megaphone, Contact, NotebookPen, ShieldCheck, Eye, Library, ListChecks, CalendarClock, Sparkles, Building2, CreditCard,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MENU = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "view_dashboard" },
  { to: "/ai", label: "AI Studio", icon: Sparkles, perm: "view_dashboard" },
  { to: "/admin-overview", label: "School", icon: ShieldCheck, perm: "manage_users", superOnly: true,
    children: [
      { to: "/admin-overview", label: "Overview", icon: ShieldCheck, perm: "manage_users" },
      { to: "/schools", label: "Schools", icon: Building2, perm: "manage_users" },
      { to: "/admin-fees", label: "Subscription Plans", icon: ReceiptIndianRupee, perm: "manage_users" },
    ],
  },
  { to: "/templates", label: "Templates", icon: LayoutTemplate, perm: "manage_users", superOnly: true },
  { to: "/leads", label: "Leads / Inquiry", icon: Megaphone, perm: "view_leads" },
  {
    to: "/students", label: "Students", icon: Users2, perm: "view_students",
    children: [
      { to: "/students", label: "All Students", icon: Users2, perm: "view_students" },
      { to: "/students/deleted", label: "Left Students", icon: Trash2, perm: "view_students" },
      { to: "/students/bulk-edit", label: "Bulk Edit", icon: Table2, perm: "edit_student" },
      { to: "/students/promote", label: "Promote / Migrate", icon: GraduationCap, perm: "edit_student" },
    ],
  },
  {
    to: "/fees-menu", label: "Fees", icon: ReceiptIndianRupee, perm: "view_fees",
    children: [
      { to: "/view-fees", label: "View Fees", icon: BarChart3, perm: "view_fees" },
      { to: "/due-fees", label: "Collect/View Fees", icon: HandCoins, perm: "view_fees" },
      { to: "/fees/collect", label: "Collect Fees", icon: HandCoins, perm: "collect_fee" },
      { to: "/fees", label: "Fee Structure", icon: ReceiptIndianRupee, perm: "view_fees" },
      { to: "/fees-card", label: "Fees Card", icon: CreditCard, perm: "view_fees" },
      { to: "/followup", label: "Fee Follow-up", icon: PhoneCall, perm: "view_followup" },
      { to: "/fee-settings", label: "Fee Setting", icon: Wallet, perm: "manage_settings" },
    ],
  },
  { to: "/parents", label: "Parent/Sibling", icon: Contact, perm: "view_fees" },
  {
    to: "/staff", label: "Staff", icon: IdCard, perm: "view_staff",
    children: [
      { to: "/staff", label: "All Staff", icon: IdCard, perm: "view_staff" },
      { to: "/staff/bulk-edit", label: "Bulk Edit Staff", icon: Table2, perm: "edit_staff" },
      { to: "/staff/deleted", label: "Deleted Staff", icon: Trash2, perm: "view_staff" },
    ],
  },
  { to: "/payroll", label: "Payroll", icon: HandCoins, perm: "view_staff" },
  {
    to: "/classes", label: "Classes", icon: School, perm: "view_classes",
    children: [
      { to: "/classes", label: "All Classes", icon: School, perm: "view_classes" },
      { to: "/classes/streams", label: "Stream", icon: Columns3, perm: "view_classes" },
    ],
  },
  {
    to: "/academics/curriculum", label: "Academics", icon: Library, perm: "view_classes",
    children: [
      { to: "/academics/curriculum", label: "Curriculum", icon: ListChecks, perm: "view_classes" },
      { to: "/academics/syllabus", label: "Syllabus", icon: FileText, perm: "view_classes" },
      { to: "/exams/subjects", label: "Subjects", icon: BookOpen, perm: "view_exams" },
    ],
  },
  { to: "/academics/timetable", label: "Timetable", icon: CalendarClock },
  { to: "/transport", label: "Transport", icon: Bus, perm: "view_transport" },
  { to: "/attendance", label: "Attendance", icon: CalendarCheck, perm: "view_attendance" },
  { to: "/homework", label: "Homework", icon: NotebookPen, perm: "view_homework" },
  { to: "/idcards", label: "ID Cards", icon: IdCard, perm: "print" },
  {
    to: "/exams", label: "Examination", icon: GraduationCap, perm: "view_exams",
    children: [
      { to: "/exams", label: "Exams & Schedules", icon: ClipboardList, perm: "view_exams" },
      { to: "/exams/marks", label: "Marks Entry", icon: PencilLine, perm: "enter_marks" },
      { to: "/exams/reports", label: "Report Cards", icon: FileText, perm: "view_exams" },
      { to: "/exams/admit-cards", label: "Admit Card", icon: IdCard, perm: "view_exams" },
    ],
  },
  { to: "/expenses", label: "Expenses", icon: Wallet, perm: "view_expenses" },
  { to: "/reports", label: "Reports", icon: BarChart3, perm: "view_reports" },
  {
    to: "/certificates", label: "Certificate", icon: Award, perm: "view_reports",
    children: [
      { to: "/certificates/transfer", label: "Transfer Certificate", icon: FileText, perm: "view_reports" },
      { to: "/certificates/character", label: "Character Certificate", icon: FileText, perm: "view_reports" },
      { to: "/certificates/bonafide", label: "Bonafide Certificate", icon: FileText, perm: "view_reports" },
      { to: "/certificates/other", label: "Other Certificates", icon: FileText, perm: "view_reports" },
    ],
  },
  { to: "/users", label: "Permissions", icon: UserCog, perm: "manage_users" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, perm: "manage_settings" },
  { to: "/audit", label: "Audit Log", icon: ScrollText, perm: "view_audit" },
  { to: "/about", label: "About Us", icon: Info, perm: "view_dashboard" },
];

function ChangePasswordDialog({ open, onOpenChange }) {
  const [oldp, setOld] = useState("");
  const [newp, setNew] = useState("");
  const save = async () => {
    try {
      await api.post("/auth/change-password", { old_password: oldp, new_password: newp });
      toast.success("Password changed");
      onOpenChange(false);
      setOld(""); setNew("");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader><DialogTitle>Change Password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Current Password</Label><Input type="password" value={oldp} onChange={(e) => setOld(e.target.value)} data-testid="old-password-input" /></div>
          <div><Label>New Password</Label><Input type="password" value={newp} onChange={(e) => setNew(e.target.value)} data-testid="new-password-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} data-testid="save-password-btn">Update Password</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { school } = useSchool();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [menuHidden, setMenuHidden] = useState(() => localStorage.getItem("menu_hidden") === "1");
  const [orientation, setOrientation] = useState(() => localStorage.getItem("menu_orientation") || "vertical");
  const [hMenu, setHMenu] = useState(null);
  const [hMenuPos, setHMenuPos] = useState({ left: 0, top: 0 });
  const openHMenu = (key, e) => {
    if (hMenu === key) { setHMenu(null); return; }
    const r = e.currentTarget.getBoundingClientRect();
    setHMenuPos({ left: r.left, top: r.bottom + 4 });
    setHMenu(key);
  };
  const toggleHidden = () => setMenuHidden((v) => { const n = !v; localStorage.setItem("menu_hidden", n ? "1" : "0"); return n; });
  const toggleOrientation = () => setOrientation((o) => { const n = o === "vertical" ? "horizontal" : "vertical"; localStorage.setItem("menu_orientation", n); return n; });

  const isSuper = user?.role === "super_admin";
  const saMode = useSAMode();
  const drill = useDrill();
  const exitDrill = () => { setDrill(null); setSAMode("super"); nav("/admin-overview"); };
  const schoolView = isSuper ? (saMode === "admin" || !!drill) : true;
  const SUPER_PATHS = ["/admin-overview", "/dashboard", "/users", "/settings", "/audit", "/about"];
  const items = MENU.filter((m) => {
    if (isSuper && !schoolView) return m.superOnly || SUPER_PATHS.includes(m.to);
    return m.superOnly ? false : hasPerm(user, m.perm);
  });
  const showSidebar = !menuHidden && orientation === "vertical";
  const showHBar = !menuHidden && orientation === "horizontal";

  const [menuQuery, setMenuQuery] = useState("");
  const flatMenu = items.flatMap((m) => m.children
    ? m.children.filter((c) => hasPerm(user, c.perm)).map((c) => ({ to: c.to, label: c.label, icon: c.icon, group: m.label }))
    : [{ to: m.to, label: m.label, icon: m.icon, group: "" }]);
  const menuResults = menuQuery.trim()
    ? flatMenu.filter((x) => (x.label + " " + x.group).toLowerCase().includes(menuQuery.trim().toLowerCase()))
    : [];

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-200">
        <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center text-white overflow-hidden shrink-0">
          {school.logo ? <img src={school.logo} alt="School logo" className="w-full h-full object-contain" data-testid="sidebar-logo" /> : <GraduationCap size={20} />}
        </div>
        <div className="min-w-0">
          <div className="font-heading font-extrabold text-lg leading-none text-slate-900 truncate" data-testid="sidebar-school-name">{school.school_name || "SSSPS"}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 truncate">{school.tag_line || "School Manager"}</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        <div className="relative mb-2">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={menuQuery} onChange={(e) => setMenuQuery(e.target.value)} placeholder="Search Menu…" data-testid="menu-search-input"
            className="menu-search-field w-full h-9 pl-9 pr-8 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition" />
          {menuQuery && <button onClick={() => setMenuQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="menu-search-clear"><X size={15} /></button>}
        </div>
        {menuQuery.trim() ? (
          <div className="space-y-0.5" data-testid="menu-search-results">
            {menuResults.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-slate-400">No menu found</div>
            ) : menuResults.map((r) => {
              const RIcon = r.icon;
              const active = loc.pathname === r.to;
              return (
                <button key={r.to} data-testid={`menu-search-${r.to.slice(1).replace(/\//g, "-")}`}
                  onClick={() => { nav(r.to); setMenuQuery(""); setOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${active ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-600 hover:bg-slate-100"}`}>
                  <RIcon size={16} />
                  <span className="flex-1 text-left">{r.label}</span>
                  {r.group && <span className="text-[10px] text-slate-400">{r.group}</span>}
                </button>
              );
            })}
          </div>
        ) : (
        items.map((m) => {
          const Icon = m.icon;
          if (m.children) {
            const kids = m.children.filter((c) => hasPerm(user, c.perm));
            const groupActive = kids.some((c) => loc.pathname === c.to)
              || loc.pathname === m.to || loc.pathname.startsWith(m.to + "/");
            const expanded = openGroup === null ? groupActive : openGroup === m.to;
            return (
              <div key={m.to}>
                <button
                  data-testid={`nav-group-${m.to.slice(1)}`}
                  onClick={() => setOpenGroup(expanded ? null : m.to)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors border-l-4 ${
                    groupActive ? "bg-indigo-50 text-indigo-700 border-indigo-600" : "text-slate-600 border-transparent hover:bg-slate-100"
                  }`}>
                  <Icon size={18} />
                  <span className="flex-1 text-left">{m.label}</span>
                  <ChevronDown size={16} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
                </button>
                {expanded && (
                  <div className="ml-4 mt-0.5 space-y-0.5 border-l border-slate-200 pl-2">
                    {kids.map((c) => {
                      const CIcon = c.icon;
                      const active = loc.pathname === c.to;
                      return (
                        <button key={c.to} data-testid={`nav-${c.to.slice(1).replace(/\//g, "-")}`}
                          onClick={() => { nav(c.to); setOpen(false); }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                            active ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-500 hover:bg-slate-100"
                          }`}>
                          <CIcon size={15} />{c.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          const active = loc.pathname === m.to || (m.to !== "/dashboard" && loc.pathname.startsWith(m.to));
          return (
            <button
              key={m.to}
              data-testid={`nav-${m.to.slice(1)}`}
              onClick={() => { nav(m.to); setOpen(false); setOpenGroup(null); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors border-l-4 ${
                active
                  ? "bg-indigo-50 text-indigo-700 border-indigo-600"
                  : "text-slate-600 border-transparent hover:bg-slate-100"
              }`}
            >
              <Icon size={18} />
              {m.label}
            </button>
          );
        })
        )}
      </nav>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      {showSidebar && (
        <aside className="menu-surface hidden lg:block fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 z-30" style={{ backgroundColor: "var(--menu-bg)" }}>
          {SidebarInner}
        </aside>
      )}

      {/* Collapsed icon rail (menu hidden) */}
      {menuHidden && (
        <aside className="menu-surface hidden lg:flex flex-col fixed inset-y-0 left-0 w-20 bg-white border-r border-slate-200 z-30" style={{ backgroundColor: "var(--menu-bg)" }} data-testid="collapsed-menu-rail">
          <div className="h-16 flex items-center justify-center border-b border-slate-200 shrink-0">
            <div className="w-11 h-11 rounded-lg bg-indigo-600 flex items-center justify-center text-white overflow-hidden">
              {school.logo ? <img src={school.logo} alt="School logo" className="w-full h-full object-contain" /> : <GraduationCap size={24} />}
            </div>
          </div>
          <nav className="flex-1 overflow-y-auto py-3 flex flex-col items-center gap-1.5">
            {items.map((m) => {
              const Icon = m.icon;
              if (m.children) {
                const kids = m.children.filter((c) => hasPerm(user, c.perm));
                const groupActive = kids.some((c) => loc.pathname === c.to) || loc.pathname === m.to || loc.pathname.startsWith(m.to + "/");
                const openK = hMenu === m.to;
                return (
                  <div key={m.to} className="relative">
                    <button onClick={() => setHMenu(openK ? null : m.to)} title={m.label} data-testid={`rail-group-${m.to.slice(1)}`}
                      className={`w-14 h-14 flex items-center justify-center rounded-xl transition-colors ${groupActive ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100"}`}>
                      <Icon size={24} />
                    </button>
                    {openK && (
                      <>
                        <div className="fixed inset-0 z-20" onClick={() => setHMenu(null)} />
                        <div className="absolute left-full top-0 ml-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[190px] z-30">
                          {kids.map((c) => {
                            const CIcon = c.icon;
                            const active = loc.pathname === c.to;
                            return (
                              <button key={c.to} data-testid={`rail-${c.to.slice(1).replace(/\//g, "-")}`} onClick={() => { nav(c.to); setHMenu(null); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${active ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-600 hover:bg-slate-100"}`}>
                                <CIcon size={14} />{c.label}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                );
              }
              const active = loc.pathname === m.to || (m.to !== "/dashboard" && loc.pathname.startsWith(m.to));
              return (
                <button key={m.to} onClick={() => nav(m.to)} title={m.label} data-testid={`rail-${m.to.slice(1)}`}
                  className={`w-14 h-14 flex items-center justify-center rounded-xl transition-colors ${active ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100"}`}>
                  <Icon size={24} />
                </button>
              );
            })}
          </nav>
        </aside>
      )}

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 bg-white shadow-xl">{SidebarInner}</div>
        </div>
      )}

      <div className={showSidebar ? "lg:pl-64" : (menuHidden ? "lg:pl-20" : "")}>
        {drill && (
          <div className="bg-amber-500 text-white px-4 lg:px-8 py-2 flex items-center justify-between gap-3 sticky top-0 z-40 no-print shadow-sm" data-testid="drill-banner">
            <span className="flex items-center gap-2 text-sm font-medium min-w-0">
              <Eye size={16} className="shrink-0" />
              <span className="truncate">Viewing <b>{school.school_name || drill.name || "school"}</b> — read-only</span>
            </span>
            <button onClick={exitDrill} className="flex items-center gap-1 bg-white/20 hover:bg-white/30 rounded-md px-3 py-1 text-xs font-semibold transition-colors shrink-0" data-testid="exit-drill-btn">
              <X size={14} />Exit School
            </button>
          </div>
        )}
        <header className="menu-surface h-16 bg-white/90 backdrop-blur border-b border-slate-200 sticky z-20 flex items-center justify-between px-4 lg:px-8 no-print" style={{ top: drill ? "2.5rem" : 0, backgroundColor: "var(--menu-bg)" }}>
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setOpen(true)} data-testid="menu-toggle">
              <Menu size={20} />
            </button>
            <button onClick={toggleHidden} className="hidden lg:inline-flex items-center justify-center p-2.5 rounded-lg text-slate-600 hover:bg-slate-100" data-testid="toggle-menu-visibility-btn" title={menuHidden ? "View Menu Bar" : "Hide Menu Bar"}>
              {menuHidden ? <PanelLeft size={26} /> : <PanelLeftClose size={26} />}
            </button>
            <button onClick={toggleOrientation} disabled={menuHidden} className="hidden lg:inline-flex items-center justify-center p-2.5 rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40" data-testid="toggle-menu-orientation-btn" title={orientation === "vertical" ? "Switch to Horizontal Menu Bar" : "Switch to Vertical Menu Bar"}>
              {orientation === "vertical" ? <Rows3 size={26} /> : <Columns3 size={26} />}
            </button>
            <div className="text-sm text-slate-500 hidden sm:block">
              {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
            {isSuper && (
              <div className="flex items-center rounded-lg bg-slate-100 p-0.5" data-testid="sa-mode-switcher">
                <button onClick={() => { setSAMode("super"); nav("/dashboard"); }} data-testid="sa-mode-super"
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${saMode === "super" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  <ShieldCheck size={13} /><span className="hidden sm:inline">Super Admin</span>
                </button>
                <button onClick={() => { setSAMode("admin"); nav("/dashboard"); }} data-testid="sa-mode-admin"
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${saMode === "admin" ? "bg-white text-emerald-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  <School size={13} /><span className="hidden sm:inline">System Admin</span>
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:block"><MenuColorSwitcher /></div>
            <div className="hidden md:block"><ThemeSwitcher /></div>
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-slate-800">{user?.name}</div>
              <div className="text-xs text-slate-400 capitalize">{user?.role}</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-semibold">
              {initials(user?.name)}
            </div>
            <button onClick={() => setPwOpen(true)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Change password" data-testid="change-pw-btn">
              <KeyRound size={18} />
            </button>
            <button onClick={logout} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" title="Logout" data-testid="logout-btn">
              <LogOut size={18} />
            </button>
          </div>
        </header>
        {showHBar && (
          <div className="menu-surface hidden lg:block bg-white border-b border-slate-200 sticky top-16 z-10 no-print" style={{ backgroundColor: "var(--menu-bg)" }} data-testid="horizontal-menu-bar">
            <div className="flex items-center gap-1 px-4 lg:px-8 py-1.5 overflow-x-auto">
              {items.map((m) => {
                const Icon = m.icon;
                if (m.children) {
                  const kids = m.children.filter((c) => hasPerm(user, c.perm));
                  const groupActive = kids.some((c) => loc.pathname === c.to) || loc.pathname === m.to || loc.pathname.startsWith(m.to + "/");
                  const openK = hMenu === m.to;
                  return (
                    <div key={m.to} className="relative">
                      <button onClick={(e) => openHMenu(m.to, e)} data-testid={`hnav-group-${m.to.slice(1)}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${groupActive ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100"}`}>
                        <Icon size={16} />{m.label}<ChevronDown size={14} className={`transition-transform ${openK ? "rotate-180" : ""}`} />
                      </button>
                      {openK && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setHMenu(null)} />
                          <div className="menu-surface fixed bg-white border border-slate-200 rounded-lg shadow-xl py-1 min-w-[200px] z-50" style={{ left: hMenuPos.left, top: hMenuPos.top, backgroundColor: "var(--menu-bg)" }}>
                            {kids.map((c) => {
                              const CIcon = c.icon;
                              const active = loc.pathname === c.to;
                              return (
                                <button key={c.to} data-testid={`hnav-${c.to.slice(1).replace(/\//g, "-")}`} onClick={() => { nav(c.to); setHMenu(null); }}
                                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${active ? "bg-indigo-50 text-indigo-700 font-medium" : "text-slate-600 hover:bg-slate-100"}`}>
                                  <CIcon size={14} />{c.label}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  );
                }
                const active = loc.pathname === m.to || (m.to !== "/dashboard" && loc.pathname.startsWith(m.to));
                return (
                  <button key={m.to} data-testid={`hnav-${m.to.slice(1)}`} onClick={() => nav(m.to)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${active ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100"}`}>
                    <Icon size={16} />{m.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <main className="p-4 lg:p-8">{children}</main>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <FingerprintPrompt />
    </div>
  );
}
